// =========================================================================
// Central content configuration for AZ Gruppe.
// Edit phone numbers, hours, address, reviews and service copy HERE.
// =========================================================================

export const site = {
  name: "AZ Gruppe",
  slogan: "Ihr Partner von A bis Z",
  tagline: "Alles aus einer Hand",
  region: "Nordrhein-Westfalen",
  regionShort: "NRW",
  email: "Info@a-z-gruppe.de",
  // Primary number used by the main "Hotline anrufen" / "Jetzt anrufen" CTAs (Büro)
  primaryPhone: "+49 173 7383528",
  primaryPhoneTel: "+491737383528",
  address: {
    line1: "Musterstraße 12",
    line2: "40213 Düsseldorf",
    region: "Nordrhein-Westfalen, Deutschland",
  },
};

// Clickable address → opens the native maps app (Apple Maps on iPhone/iPad/Mac,
// Google Maps everywhere else).
const isApplePlatform =
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
const buildMapsUrl = () => {
  const mapsQuery = encodeURIComponent(
    `${site.address.line1}, ${site.address.line2}, Deutschland`
  );
  return isApplePlatform
    ? `https://maps.apple.com/?q=${mapsQuery}`
    : `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
};
export let mapsUrl = buildMapsUrl();

export const refreshDerivedSiteConfig = () => {
  mapsUrl = buildMapsUrl();
  const digits = (value) => `+${String(value || "").replace(/\D/g, "")}`;
  site.primaryPhoneTel = digits(site.primaryPhone);
  hotlines.forEach((hotline) => {
    hotline.tel = digits(hotline.number);
  });
};

export const logos = {
  src: "/az-logo.png",
};

export const hotlines = [
  {
    id: "gutachten-zulassung",
    label: "Gutachten & Zulassung Hotline",
    number: "+49 1512 9895163",
    tel: "+4915129895163",
    testid: "topbar-gutachten-zulassung-hotline-link",
  },
  {
    id: "versicherung",
    label: "Versicherung Hotline",
    number: "+49 176 43261918",
    tel: "+4917643261918",
    testid: "topbar-versicherung-hotline-link",
  },
  {
    id: "buero",
    label: "Büro",
    number: "+49 173 7383528",
    tel: "+491737383528",
    testid: "topbar-buero-link",
  },
];

export const openingHours = [
  { days: "Mo – Fr", time: "08:00 – 18:00 Uhr" },
  { days: "Sa", time: "12:00 – 19:00 Uhr" },
];

export const navLinks = [
  { label: "Startseite", to: "/", testid: "nav-startseite-link" },
  { label: "Über uns", to: "/ueber-uns", testid: "nav-ueber-uns-link" },
  { label: "Partnernetzwerk", to: "/partnernetzwerk", testid: "nav-partnernetzwerk-link" },
  { label: "Kontakt", to: "/kontakt", testid: "nav-kontakt-link" },
];

// Form categories (Auswahlbereich). Values must match backend ALLOWED_CATEGORIES.
export const categories = [
  { value: "gutachten", label: "Gutachten" },
  { value: "versicherung", label: "Versicherung" },
  { value: "kfz-zulassung", label: "Kfz-Zulassung" },
  { value: "services", label: "Services" },
  { value: "allgemeine-anfrage", label: "Allgemeine Anfrage" },
];

// The four service areas (used in services preview + Partnernetzwerk page).
export const services = [
  {
    id: "gutachten",
    icon: "ClipboardCheck",
    title: "Gutachten",
    short: "Professionelle Kfz-Gutachten, Wertermittlung und Begleitung im Schadenprozess.",
    description:
      "Wir erstellen professionelle Kfz-Schadengutachten, Wertgutachten und eine vollständige Unfalldokumentation – und begleiten Sie zuverlässig durch den gesamten Schadenprozess. Als Gutachter setzen wir uns dafür ein, dass Ihre Ansprüche korrekt erfasst und durchgesetzt werden.",
    bullets: [
      "Schadengutachten nach Unfall",
      "Wertgutachten & Fahrzeugbewertung",
      "Vollständige Unfalldokumentation",
      "Begleitung der gesamten Schadenabwicklung",
    ],
    image: "/images/home/service-gutachten.png",
    alt: "Sachverständiger dokumentiert einen Fahrzeugschaden mit Unterlagen",
  },
  {
    id: "versicherung",
    icon: "ShieldCheck",
    title: "Versicherung",
    short: "Alle Versicherungsarten – bestehende Verträge geprüft und transparent optimiert.",
    description:
      "Wir betreuen alle Arten von Versicherungen. Bestehende Verträge werden sorgfältig geprüft, Optimierungsmöglichkeiten analysiert und bessere Lösungen transparent für Sie gesucht. So zahlen Sie nie mehr als nötig – und sind dennoch umfassend abgesichert.",
    bullets: [
      "Alle Versicherungsarten aus einer Hand",
      "Prüfung Ihrer bestehenden Verträge",
      "Analyse von Optimierungsmöglichkeiten",
      "Transparente Suche nach besseren Lösungen",
    ],
    image: "/images/home/service-versicherung.png",
    alt: "Persönliche Versicherungsberatung mit Handschlag im Büro",
  },
  {
    id: "kfz-zulassung",
    icon: "Car",
    title: "Kfz-Zulassung",
    short: "Fahrzeugzulassung, Formalitäten und Dokumente – einfach und unkompliziert.",
    description:
      "Wir übernehmen die Fahrzeugzulassung, unterstützen Sie bei allen Formalitäten und bereiten die notwendigen Dokumente vor. Die gesamte Abwicklung rund um Ihr Fahrzeug wird so spürbar einfacher – ohne Wartezeiten und ohne Papierkram für Sie.",
    bullets: [
      "An-, Um- und Abmeldung von Fahrzeugen",
      "Unterstützung bei allen Formalitäten",
      "Vorbereitung sämtlicher Dokumente",
      "Vereinfachte, schnelle Abwicklung",
    ],
    image: "/images/home/service-kfz-zulassung.png",
    alt: "Unterzeichnung von Fahrzeugdokumenten am Schreibtisch",
  },
  {
    id: "services",
    icon: "Sparkles",
    title: "Services",
    short: "Vertrags- und Servicebetreuung im Alltag – inkl. Strom- und Gasverträge.",
    description:
      "Wir begleiten Sie bei allen alltäglichen Vertrags- und Serviceangelegenheiten – von Strom- und Gasverträgen bis zur Optimierung bestehender Verträge. Als langfristiger Partner kümmern wir uns persönlich um die Details, damit Sie Zeit, Geld und Aufwand sparen.",
    bullets: [
      "Strom- und Gasverträge",
      "Optimierung bestehender Verträge",
      "Allgemeine Vertrags- und Servicebetreuung",
      "Langfristige, persönliche Begleitung",
    ],
    image: "/images/home/service-services.png",
    alt: "Beraterin bespricht Verträge mit Kunden am Laptop",
  },
];

export const benefits = [
  {
    icon: "Handshake",
    title: "Aus einer Hand",
    text: "Versicherung, Gutachten, Zulassung und Verträge – ein Ansprechpartner für alles.",
  },
  {
    icon: "Eye",
    title: "Transparente Beratung",
    text: "Klare Empfehlungen ohne Umwege – Sie verstehen jeden Schritt.",
  },
  {
    icon: "Clock",
    title: "Schnelle Bearbeitung",
    text: "Wir erledigen den Papierkram zügig, damit Sie Zeit sparen.",
  },
  {
    icon: "Lock",
    title: "Datenschutz & Diskretion",
    text: "Ihre Daten und Anliegen behandeln wir vertraulich und sicher.",
  },
  {
    icon: "PiggyBank",
    title: "Kosten sparen",
    text: "Wir prüfen laufend Optimierungen, damit Sie nicht zu viel zahlen.",
  },
  {
    icon: "HeartHandshake",
    title: "Langfristige Betreuung",
    text: "Wir begleiten Sie dauerhaft – persönlich und verlässlich.",
  },
];

export const reviews = {
  rating: "4.9",
  scale: "5",
  count: "120+",
  items: [
    {
      name: "Sandra M.",
      city: "Düsseldorf",
      stars: 5,
      text: "Nach meinem Unfall hat sich AZ Gruppe um wirklich alles gekümmert – Gutachten, Versicherung und Papierkram. Absolut zuverlässig und persönlich.",
    },
    {
      name: "Thomas K.",
      city: "Köln",
      stars: 5,
      text: "Endlich ein Ansprechpartner für alles. Meine Verträge wurden geprüft und ich spare jetzt jeden Monat. Sehr transparente Beratung.",
    },
    {
      name: "Aylin D.",
      city: "Essen",
      stars: 5,
      text: "Die Fahrzeugzulassung lief komplett ohne Stress. Schnell, freundlich und unkompliziert. Klare Empfehlung!",
    },
    {
      name: "Markus B.",
      city: "Dortmund",
      stars: 5,
      text: "Kompetent und ehrlich. Man merkt, dass hier alles aus einer Hand kommt. Ich fühle mich rundum gut betreut.",
    },
  ],
};

// =========================================================================
// Header social-media & quick-contact links (CMS-ready).
// =========================================================================
export const headerContactMessage =
  "Guten Tag, ich habe Ihre Website besucht und möchte gerne weitere Informationen erhalten.";

export const socialLinks = [
  {
    id: "facebook",
    icon: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/people/AZ-Group/61590915295919/",
    external: true,
  },
  {
    id: "instagram",
    icon: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/azgruppe.de/",
    external: true,
  },
  {
    id: "tiktok",
    icon: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@azgruppe.de",
    external: true,
  },
  { id: "email", icon: "email", label: "E-Mail", href: "mailto:Info@a-z-gruppe.de", external: false },
  {
    id: "whatsapp-zulassung",
    icon: "whatsapp",
    badge: "car",
    label: "WhatsApp Zulassung & Gutachten",
    href: `https://wa.me/4915129895163?text=${encodeURIComponent(headerContactMessage)}`,
    external: true,
  },
  {
    id: "whatsapp-versicherung",
    icon: "whatsapp",
    badge: "shield",
    label: "WhatsApp Versicherung & Energie",
    href: `https://wa.me/4917643261918?text=${encodeURIComponent(headerContactMessage)}`,
    external: true,
  },
];

// =========================================================================
// Homepage editorial content (CMS-ready). Edit headings, copy, images and
// the media URLs here. All text is real HTML and fully translation-safe.
// =========================================================================

// Full-width parallax statement band (inspired by premium corporate sites).
export const homeStatement = {
  eyebrow: "Was uns antreibt",
  title: "Alles aus einer Hand – persönlich gedacht.",
  text: "Wir denken in Lösungen, nicht in Zuständigkeiten. Versicherung, Gutachten, Zulassung und Verträge greifen bei uns ineinander – damit Sie nur einen Ansprechpartner brauchen und sich um den Rest nicht kümmern müssen.",
  image:
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80",
  alt: "Modernes Bürogebäude als Sinnbild für verlässliche Zusammenarbeit",
};

// Alternating image + text showcase blocks.
export const homeShowcase = [
  {
    id: "warum",
    eyebrow: "Warum AZ Gruppe",
    title: "Ein verlässlicher Partner – von A bis Z.",
    text: "Wir sind kein reiner Vermittler. Wir kümmern uns selbst um Ihre Anliegen, prüfen sorgfältig und behalten Ihre Vorteile im Blick. So entsteht eine Betreuung, die langfristig trägt – transparent und auf Augenhöhe.",
    bullets: [
      "Ein Ansprechpartner für alle Bereiche",
      "Transparente, ehrliche Beratung",
      "Persönliche Begleitung statt Warteschleife",
      "Regelmäßige Optimierung Ihrer Verträge",
    ],
    image: "/images/home/warum-az.png",
    alt: "Beratungsteam der AZ Gruppe im Gespräch am Konferenztisch",
    reverse: false,
    stat: { value: "1", label: "Ansprechpartner für alles" },
  },
  {
    id: "betreuung",
    eyebrow: "Persönliche Betreuung",
    title: "Menschen verbinden, Anliegen lösen.",
    text: "Hinter jeder Anfrage steht ein Mensch mit einem konkreten Anliegen. Genau dort setzen wir an: mit kurzen Wegen, klaren Absprachen und einem Team, das zuhört und zuverlässig handelt.",
    bullets: [
      "Schnelle Rückmeldung auf Ihre Anfrage",
      "Verständliche Erklärungen ohne Fachchinesisch",
      "Diskreter Umgang mit Ihren Daten",
      "Langfristige Begleitung – auch nach dem Abschluss",
    ],
    image: "/images/home/warum-az-pe.png",
    alt: "Persönliches Beratungsgespräch mit Kundin im Büro der AZ Gruppe",
    reverse: true,
    stat: { value: "100%", label: "Persönliche Betreuung" },
  },
];

// Premium video block (poster + lazy, click-to-play to keep the page fast).
export const homeVideo = {
  eyebrow: "AZ Gruppe in Bewegung",
  title: "Ein Partner. Alle Wege.",
  text: "Von der ersten Beratung bis zur finalen Abwicklung begleiten wir Sie persönlich – ruhig, verlässlich und mit Blick für das Ganze.",
  caption: "Persönliche Beratung & Betreuung",
  src: "/videos/hero-video.mp4",
  poster:
    "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&q=80",
};

// Trust / stats band (numbers count up — translation-safe).
export const homeStats = [
  {
    value: 4,
    decimals: 0,
    suffix: "",
    label: "Leistungsbereiche",
    hint: "Gutachten · Versicherung · Zulassung · Services",
  },
  {
    value: 120,
    decimals: 0,
    suffix: "+",
    label: "Google-Bewertungen",
    hint: "von zufriedenen Kundinnen und Kunden",
  },
  {
    value: 4.9,
    decimals: 1,
    suffix: " / 5",
    label: "Durchschnittsbewertung",
    hint: "für Service und Verlässlichkeit",
  },
  {
    value: 1,
    decimals: 0,
    suffix: "",
    label: "Ansprechpartner",
    hint: "alles aus einer Hand",
  },
];
